import * as React from "react";
import { PropertyType } from "flavr-common";
import { modelPool } from "@modules/model";
import { HIGHLIGHT_TIMEOUT_MS, isNumberArray, NUMBER_DECIMALS } from "../modelTreeUtils";
import { BooleanToggleField, ColorField, EnumField, Number16Field, NumberField, NumberTupleField, TextField } from "./FacetEditorFields.component";
export function ModelPropertiesView({ model, autoUpdate, onModelChanged, }) {
    var _a;
    const [generalCollapsed, setGeneralCollapsed] = React.useState(false);
    if (model === undefined) {
        return (React.createElement("div", { className: "model-properties-empty" },
            React.createElement("em", null, "No model selected")));
    }
    const props = [...model.properties];
    const facets = model.facets;
    const visibleIndex = props.findIndex((prop) => prop.name === "visible");
    const lifecycleProps = [
        {
            name: "Created",
            value: formatTimestamp(model.createdAt),
            changedAt: undefined,
            changeType: "unchanged"
        },
        {
            name: "Modified",
            value: formatTimestamp(model.modifiedAt),
            changedAt: undefined,
            changeType: "unchanged"
        }
    ];
    if (visibleIndex >= 0) {
        props.splice(visibleIndex + 1, 0, ...lifecycleProps);
    }
    else {
        props.push(...lifecycleProps);
    }
    const facetPropertiesBlacklist = ["type", "subType"];
    const visibleFacets = facets.map((facet) => (Object.assign(Object.assign({}, facet), { properties: facet.properties.filter((prop) => !facetPropertiesBlacklist.includes(prop.name)) })));
    const liveModel = modelPool.getModel(model.uid);
    const liveFacets = (_a = liveModel === null || liveModel === void 0 ? void 0 : liveModel.getFacets()) !== null && _a !== void 0 ? _a : [];
    const facetPropertyClass = (facet) => {
        const changeClass = getChangeClassName(facet.changedAt, facet.changeType, autoUpdate);
        return "facet-properties-entry " + changeClass;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "model-properties-heading collapsible", onClick: () => setGeneralCollapsed((prev) => !prev) },
            React.createElement("span", { className: `collapse-arrow ${generalCollapsed ? "collapsed" : ""}` }, "\u25BC"),
            React.createElement("span", null, "General")),
        !generalCollapsed && (React.createElement("table", { className: "model-properties-table" },
            React.createElement("tbody", null, props.map((prop) => (React.createElement("tr", { key: prop.name, className: getChangeClassName(prop.changedAt, prop.changeType, autoUpdate) },
                React.createElement("td", { className: "model-properties-key" }, prop.name),
                React.createElement("td", { className: "model-properties-value" }, renderPropertyValue(prop.value, 0, autoUpdate)))))))),
        React.createElement("h5", { className: "model-properties-heading" }, "Facets"),
        React.createElement("div", { className: "model-properties-content" },
            visibleFacets.map((facet) => (React.createElement("div", { key: facet.name, className: facetPropertyClass(facet) },
                React.createElement("div", { className: "facet-name" }, facet.name),
                facet.properties.length > 0 ? (React.createElement("div", { className: "facet-properties" }, renderFacetProperties(model.uid, liveFacets.find((it) => it.type === facet.name), facet, autoUpdate, onModelChanged))) : (React.createElement("div", { className: "facet-properties-empty" },
                    React.createElement("em", null, "No properties")))))),
            visibleFacets.length === 0 ? React.createElement("div", { className: "model-properties-empty" }, "No Facets") : null)));
}
function renderFacetProperties(modelUID, liveFacet, facetInfo, autoUpdate, onModelChanged) {
    const facetType = liveFacet === null || liveFacet === void 0 ? void 0 : liveFacet.constructor;
    const propTypes = facetType === null || facetType === void 0 ? void 0 : facetType.propTypes;
    if (liveFacet === undefined || propTypes === undefined) {
        return renderPropertyValue(facetInfo.properties, 0, autoUpdate);
    }
    return (React.createElement("table", { className: "model-subtable" },
        React.createElement("tbody", null, facetInfo.properties.map((prop, i) => {
            const descriptor = propTypes[prop.name];
            return (React.createElement("tr", { key: `${prop.name}${i}`, className: getChangeClassName(prop.changedAt, prop.changeType, autoUpdate) },
                React.createElement("td", { className: "model-subtable-key" }, prop.name),
                React.createElement("td", { className: "model-subtable-value" }, descriptor !== undefined
                    ? renderEditableValue(modelUID, liveFacet, descriptor, [prop.name], liveFacet[prop.name], prop, autoUpdate, onModelChanged)
                    : renderPropertyValue(prop.value, 0, autoUpdate))));
        }))));
}
function renderEditableValue(modelUID, facet, descriptor, path, currentValue, propertyInfo, autoUpdate, onModelChanged) {
    const updateFacetValue = (nextValue) => {
        const model = modelPool.getModel(modelUID);
        if (model === undefined)
            return;
        const nextFacet = facet.clone();
        setValueAtPath(nextFacet, path, nextValue);
        model.setFacet(nextFacet);
        onModelChanged();
    };
    if (isSafeDescriptor(descriptor)) {
        const isUndefined = currentValue === undefined;
        const baseDescriptor = unwrapSafeDescriptor(descriptor);
        const previewValue = isUndefined ? createDefaultValueForDescriptor(baseDescriptor) : currentValue;
        return (React.createElement(SafeValuePicker, { isUndefined: isUndefined, onModeChange: (nextMode) => {
                if (nextMode === "undefined") {
                    updateFacetValue(undefined);
                }
                else {
                    updateFacetValue(createDefaultValueForDescriptor(baseDescriptor));
                }
            } }, renderEditableValue(modelUID, facet, baseDescriptor, path, previewValue, propertyInfo, autoUpdate, onModelChanged)));
    }
    switch (descriptor.type) {
        case PropertyType.Boolean:
            if (typeof currentValue !== "boolean") {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(BooleanToggleField, { value: currentValue, onChange: updateFacetValue });
        case PropertyType.Int8:
        case PropertyType.Uint8:
        case PropertyType.Int16:
        case PropertyType.Uint16:
        case PropertyType.Int32:
        case PropertyType.Uint32:
        case PropertyType.Float32:
        case PropertyType.Float64:
            if (typeof currentValue !== "number") {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(NumberField, { value: currentValue, onCommit: updateFacetValue });
        case PropertyType.ColorHex:
            if (typeof currentValue !== "number") {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(ColorField, { value: currentValue, onCommit: updateFacetValue });
        case PropertyType.Utf16String:
        case PropertyType.AssetPath:
            if (typeof currentValue !== "string") {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(TextField, { value: currentValue, onCommit: updateFacetValue });
        case PropertyType.EnumUint8:
            if (typeof currentValue !== "number") {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return (React.createElement(EnumField, { value: currentValue, options: Object.entries(descriptor.items).map(([value, label]) => ({
                    value: Number(value),
                    label
                })), onChange: updateFacetValue }));
        case PropertyType.Number3:
            if (!isNumberArray(currentValue) || currentValue.length !== 3) {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(NumberTupleField, { value: currentValue, length: 3, onCommit: updateFacetValue });
        case PropertyType.Number4:
            if (!isNumberArray(currentValue) || currentValue.length !== 4) {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(NumberTupleField, { value: currentValue, length: 4, onCommit: updateFacetValue });
        case PropertyType.Number16:
            if (!isNumberArray(currentValue) || currentValue.length !== 16) {
                return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
            }
            return React.createElement(Number16Field, { value: currentValue, onCommit: updateFacetValue });
        case PropertyType.Array: {
            const childProperties = isPropertyArray(propertyInfo.value) ? propertyInfo.value : [];
            return (React.createElement("table", { className: "model-subtable" },
                React.createElement("tbody", null, childProperties.map((childProp, index) => {
                    const childValue = Array.isArray(currentValue) ? currentValue[index] : undefined;
                    return (React.createElement("tr", { key: `${childProp.name}${index}`, className: getChangeClassName(childProp.changedAt, childProp.changeType, autoUpdate) },
                        React.createElement("td", { className: "model-subtable-key" }, childProp.name),
                        React.createElement("td", { className: "model-subtable-value" }, renderEditableValue(modelUID, facet, descriptor.elementType, [...path, index], childValue, childProp, autoUpdate, onModelChanged))));
                }))));
        }
        case PropertyType.Struct: {
            const childProperties = isPropertyArray(propertyInfo.value) ? propertyInfo.value : [];
            return (React.createElement("table", { className: "model-subtable" },
                React.createElement("tbody", null, childProperties.map((childProp, index) => {
                    const childDescriptor = descriptor.properties[childProp.name];
                    const childValue = currentValue === null || currentValue === void 0 ? void 0 : currentValue[childProp.name];
                    return (React.createElement("tr", { key: `${childProp.name}${index}`, className: getChangeClassName(childProp.changedAt, childProp.changeType, autoUpdate) },
                        React.createElement("td", { className: "model-subtable-key" }, childProp.name),
                        React.createElement("td", { className: "model-subtable-value" }, childDescriptor !== undefined
                            ? renderEditableValue(modelUID, facet, childDescriptor, [...path, childProp.name], childValue, childProp, autoUpdate, onModelChanged)
                            : renderPropertyValue(childProp.value, 1, autoUpdate))));
                }))));
        }
        default:
            return renderPropertyValue(propertyInfo.value, 0, autoUpdate);
    }
}
function SafeValuePicker({ isUndefined, onModeChange, children, }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const containerRef = React.useRef(null);
    React.useEffect(() => {
        if (!isOpen)
            return;
        const handlePointerDown = (event) => {
            var _a;
            if (((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) === true)
                return;
            setIsOpen(false);
        };
        window.addEventListener("mousedown", handlePointerDown);
        return () => {
            window.removeEventListener("mousedown", handlePointerDown);
        };
    }, [isOpen]);
    return (React.createElement("div", { ref: containerRef, className: "safe-value-picker" },
        React.createElement("div", { className: "safe-value-picker__field" },
            React.createElement("div", { className: isOpen ? "safe-value-picker__current safe-value-picker__current--hidden" : "safe-value-picker__current" }, isUndefined ? (React.createElement("div", { className: "safe-value-picker__undefined" },
                React.createElement("em", null, "undefined"))) : (children)),
            isOpen && (React.createElement("div", { className: "safe-value-picker__panel" },
                React.createElement("button", { type: "button", className: "safe-value-picker__option", onClick: () => {
                        onModeChange("value");
                        setIsOpen(false);
                    } },
                    React.createElement("div", { className: "safe-value-picker__option-preview" }, children)),
                React.createElement("button", { type: "button", className: "safe-value-picker__option", onClick: () => {
                        onModeChange("undefined");
                        setIsOpen(false);
                    } },
                    React.createElement("em", null, "undefined"))))),
        React.createElement("button", { type: "button", className: "safe-value-picker__toggle facet-enum-select", onClick: () => setIsOpen((prev) => !prev), "aria-haspopup": "listbox", "aria-expanded": isOpen }, "\u25BC")));
}
function renderPropertyValue(propValue, depth, autoUpdate) {
    if (propValue === undefined) {
        return React.createElement("em", null, "undefined");
    }
    if (propValue === null) {
        return React.createElement("em", null, "null");
    }
    if (typeof propValue === "string") {
        return React.createElement("span", null, propValue);
    }
    if (isNumberArray(propValue)) {
        if (propValue.length === 16)
            return renderMatrix4(propValue);
        if (propValue.length < 16) {
            return React.createElement("span", null, propValue.map(x => x.toFixed(NUMBER_DECIMALS)).join(", "));
        }
        return React.createElement("span", null,
            "Array(",
            propValue.length,
            ")");
    }
    if (!Array.isArray(propValue)) {
        return React.createElement("span", null, String(propValue));
    }
    return (React.createElement("table", { className: "model-subtable" },
        React.createElement("tbody", null, propValue.map((prop, i) => (React.createElement("tr", { key: `${prop.name}${i}`, className: getChangeClassName(prop.changedAt, prop.changeType, autoUpdate) },
            React.createElement("td", { className: "model-subtable-key" }, prop.name),
            React.createElement("td", { className: "model-subtable-value" }, renderPropertyValue(prop.value, depth, autoUpdate))))))));
}
function renderMatrix4(values) {
    return (React.createElement("table", { className: "model-subtable matrix" },
        React.createElement("tbody", null, [0, 1, 2, 3].map((row) => (React.createElement("tr", { key: row }, [0, 1, 2, 3].map((col) => (React.createElement("td", { key: col, className: "matrix-element" }, values[row * 4 + col].toFixed(NUMBER_DECIMALS))))))))));
}
const getChangeClassName = (changedAt, changeType, autoUpdate) => {
    if (changedAt === undefined)
        return "";
    if (!autoUpdate)
        return `property-${changeType}`;
    return (Date.now() - changedAt < HIGHLIGHT_TIMEOUT_MS)
        ? `property-${changeType}`
        : "";
};
const formatTimestamp = (timestamp) => {
    if (timestamp === undefined)
        return "unknown";
    return new Date(timestamp).toLocaleString();
};
function setValueAtPath(target, path, value) {
    let current = target;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (current[key] === undefined) {
            current[key] = typeof path[i + 1] === "number" ? [] : {};
        }
        current = current[key];
    }
    current[path[path.length - 1]] = value;
}
function isPropertyArray(value) {
    return Array.isArray(value) && (value.length === 0 || !isNumberArray(value));
}
function isSafeDescriptor(descriptor) {
    switch (descriptor.type) {
        case PropertyType.BooleanSafe:
        case PropertyType.UidSafe:
        case PropertyType.Uint8Safe:
        case PropertyType.Uint16Safe:
        case PropertyType.Uint32Safe:
        case PropertyType.Float32Safe:
        case PropertyType.Float64Safe:
        case PropertyType.Utf16StringSafe:
        case PropertyType.ArraySafe:
        case PropertyType.DictionarySafe:
        case PropertyType.StructSafe:
        case PropertyType.Number3Safe:
        case PropertyType.Number4Safe:
        case PropertyType.Number16Safe:
        case PropertyType.ObjectSafe:
        case PropertyType.EnumUint8Safe:
        case PropertyType.ColorHexSafe:
        case PropertyType.AssetPathSafe:
        case PropertyType.GroupUint32Safe:
            return true;
        default:
            return false;
    }
}
function unwrapSafeDescriptor(descriptor) {
    return Object.assign(Object.assign({}, descriptor), { type: getNonSafePropertyType(descriptor.type) });
}
function getNonSafePropertyType(type) {
    switch (type) {
        case PropertyType.BooleanSafe:
            return PropertyType.Boolean;
        case PropertyType.UidSafe:
            return PropertyType.Uid;
        case PropertyType.Uint8Safe:
            return PropertyType.Uint8;
        case PropertyType.Uint16Safe:
            return PropertyType.Uint16;
        case PropertyType.Uint32Safe:
            return PropertyType.Uint32;
        case PropertyType.Float32Safe:
            return PropertyType.Float32;
        case PropertyType.Float64Safe:
            return PropertyType.Float64;
        case PropertyType.Utf16StringSafe:
            return PropertyType.Utf16String;
        case PropertyType.ArraySafe:
            return PropertyType.Array;
        case PropertyType.DictionarySafe:
            return PropertyType.Dictionary;
        case PropertyType.StructSafe:
            return PropertyType.Struct;
        case PropertyType.Number3Safe:
            return PropertyType.Number3;
        case PropertyType.Number4Safe:
            return PropertyType.Number4;
        case PropertyType.Number16Safe:
            return PropertyType.Number16;
        case PropertyType.ObjectSafe:
            return PropertyType.Object;
        case PropertyType.EnumUint8Safe:
            return PropertyType.EnumUint8;
        case PropertyType.ColorHexSafe:
            return PropertyType.ColorHex;
        case PropertyType.AssetPathSafe:
            return PropertyType.AssetPath;
        case PropertyType.GroupUint32Safe:
            return PropertyType.GroupUint32;
        default:
            return type;
    }
}
function createDefaultValueForDescriptor(descriptor) {
    if ("default" in descriptor && descriptor.default !== undefined) {
        return cloneDescriptorDefault(descriptor.default);
    }
    switch (descriptor.type) {
        case PropertyType.Boolean:
            return false;
        case PropertyType.Uid:
        case PropertyType.Int8:
        case PropertyType.Uint8:
        case PropertyType.Int16:
        case PropertyType.Uint16:
        case PropertyType.Int32:
        case PropertyType.Uint32:
        case PropertyType.Float32:
        case PropertyType.Float64:
        case PropertyType.EnumUint8:
        case PropertyType.ColorHex:
        case PropertyType.GroupUint32:
            return 0;
        case PropertyType.Utf16String:
        case PropertyType.AssetPath:
            return "";
        case PropertyType.Number3:
            return [0, 0, 0];
        case PropertyType.Number4:
            return [0, 0, 0, 0];
        case PropertyType.Number16:
            return Array.from({ length: 16 }, () => 0);
        case PropertyType.Array:
            return [];
        case PropertyType.Dictionary:
            return {};
        case PropertyType.Object:
            return {};
        case PropertyType.Struct: {
            const result = {};
            for (const [key, childDescriptor] of Object.entries(descriptor.properties)) {
                result[key] = createDefaultValueForDescriptor(childDescriptor);
            }
            return result;
        }
        default:
            return null;
    }
}
function cloneDescriptorDefault(value) {
    if (Array.isArray(value)) {
        return value.map((entry) => cloneDescriptorDefault(entry));
    }
    if (value !== null && typeof value === "object") {
        const result = {};
        for (const [key, entry] of Object.entries(value)) {
            result[key] = cloneDescriptorDefault(entry);
        }
        return result;
    }
    return value;
}
